import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router';
import { moetTeamnaamKiezen } from '@ame/shared';
import { useHelpRequired, useSchermPad } from '@/components/hooks';
import { Laadscherm } from '@/components/Laadscherm';
import { Melding } from '@/components/Melding';
import { Verbindingsmelding } from '@/components/Verbindingsmelding';
import { NIETS_GEROLD, PlayerContext, SpelProvider, useSpel, type Gerold } from '@/spel';

const ResultsPopup = lazy(() => import('@/screens/ResultsPopup'));
const TeamnaamKiezen = lazy(() => import('@/screens/Teamnaam'));
const Eindstand = lazy(() => import('@/screens/Eindstand'));
const Introductie = lazy(() => import('@/screens/Introductie'));

/**
 * De omgeving van alle spelschermen onder `/:gameId/:userId`, zoals `Layout.tsx` van het
 * origineel: verbindt met het spel, wacht tot spel en speler er zijn, geeft ze via
 * `PlayerContext` aan de schermen en toont aan het eind van een ronde de resultaten.
 *
 * Niet in het origineel: zolang het team geen naam heeft, staat in plaats van de schermen
 * het kiezen van de teamnaam. Daarna volgt in week 1 een korte introductie van de directeur.
 * Is het spel afgelopen, dan ligt de eindstand over alles heen.
 */
export function Layout() {
  const { gameId = '' } = useParams();
  return (
    <SpelProvider spelId={gameId}>
      <SpelerOmgeving />
      <Verbindingsmelding />
    </SpelProvider>
  );
}

/** Het deel binnen de `SpelProvider`; los te gebruiken in de tests. */
export function SpelerOmgeving() {
  const { userId } = useParams();
  const { staat, status } = useSpel();
  const game = staat?.game;
  const player = staat?.players.players.find((speler) => speler._id === userId);

  // Welke assets deze ronde gegooid zijn; bij een nieuwe ronde weer niets. Bijgewerkt
  // tijdens het renderen in plaats van in een effect, zodat er geen ronde met oude waarden
  // tussen zit.
  const ronde = game?.round ?? 0;
  const [gerold, setGerold] = useState(NIETS_GEROLD);
  const [rondeVanGerold, setRondeVanGerold] = useState(ronde);
  if (ronde !== rondeVanGerold) {
    setRondeVanGerold(ronde);
    setGerold(NIETS_GEROLD);
  }

  // Een geluidje zolang de speler op een ander scherm iets moet doen.
  const pad = useSchermPad();
  const [hulpNodig, hulpPad] = useHelpRequired(game, player);
  const geluid = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    if (!geluid.current) {
      geluid.current = new Audio('/audio/notification.mp3');
      geluid.current.volume = 0.2;
    }
    if (hulpNodig && hulpPad !== pad) {
      // De browser weigert geluid zolang er nog niet op de pagina is geklikt.
      geluid.current.play().catch(() => undefined);
    } else {
      geluid.current.pause();
    }
  }, [hulpNodig, hulpPad, pad]);

  const waarde = useMemo(
    () =>
      game && player
        ? { game, player, rolledState: [gerold, setGerold] as [Gerold, typeof setGerold] }
        : undefined,
    [game, player, gerold],
  );

  if (status === 'onbekend') {
    return <Melding titel="Dit spel bestaat niet (meer)." tekst="Controleer de spelcode." />;
  }
  if (staat && !player) {
    return <Melding titel="Deze speler hoort niet bij dit spel." tekst="Log opnieuw in." />;
  }
  if (!waarde) return <Laadscherm />;

  const { game: spel } = waarde;
  if (moetTeamnaamKiezen(spel)) {
    return (
      <PlayerContext value={waarde}>
        <Suspense fallback={<Laadscherm />}>
          <TeamnaamKiezen />
        </Suspense>
      </PlayerContext>
    );
  }

  return (
    <PlayerContext value={waarde}>
      <Outlet />
      {spel.round === 0 && !spel.roundOver.roundOver && !spel.afgelopen && (
        <Suspense fallback={null}>
          <Introductie game={spel} />
        </Suspense>
      )}
      {spel.afgelopen ? (
        <Suspense fallback={null}>
          <Eindstand game={spel} />
        </Suspense>
      ) : (
        spel.roundOver.roundOver &&
        spel.effectQueue.length === 0 && (
          <Suspense fallback={null}>
            <ResultsPopup game={spel} />
          </Suspense>
        )
      )}
    </PlayerContext>
  );
}
